<template>
  <div 
    class="location-selector-card"
    :class="{ 'location-selected': hasLocationSet }"
    @click="openModal()"
    id="modal-button"
    role="button"
    tabindex="0"
    @keydown.enter="openModal()"
    @keydown.space.prevent="openModal()"
  >
    <!-- Empty State -->
    <div v-if="!hasLocationSet" class="location-empty-state">
      <div class="location-icon-container">
        <svg class="location-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
        </svg>
      </div>
      <div class="location-text-content">
        <span class="location-action-text">{{ t('views.standardized_location.click_to_select_location') }}</span>
        <span class="location-helper-text">{{ t('views.standardized_location.location_helper_text') }}</span>
      </div>
      <div class="location-chevron">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>

    <!-- Selected State -->
    <div v-else class="location-selected-state">
      <div class="location-icon-container selected">
        <svg class="location-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
        </svg>
        <div class="checkmark-badge">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
          </svg>
        </div>
      </div>
      <div class="location-text-content">
        <span class="location-confirmed-text">{{ t('views.standardized_location.location_confirmed') }}</span>
        <span class="location-edit-text">{{ t('views.standardized_location.click_to_update_location') }}</span>
      </div>
      <div class="location-chevron">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import ModalService from '@services/ModalService'
import GlobalEventBus from '../services/GlobalEventBus'
import i18n from '@utils/i18n'

const { t } = i18n.global

const props = defineProps({
  pointData: {
    type: Object,
  }
})

const lng = ref('')
const lat = ref('')
const position = ref('')

// Computed properties
const hasExistingLocation = computed(() => {
  return props.pointData?.entity?.location_lat && 
         props.pointData?.entity?.location_long
})

// Tracks if a location is currently set (either from initial data or user selection)
const hasLocationSet = computed(() => {
  return lat.value && lng.value
})

onMounted(() => {
  // Listen for location updates from the standardized location modal
  GlobalEventBus.subscribe('location-saved', locationData => {
    if (locationData.lat && locationData.lng) {
      lat.value = parseFloat(locationData.lat)
      lng.value = parseFloat(locationData.lng)
      position.value = `${lat.value.toFixed(4)}, ${lng.value.toFixed(4)}`
      setHiddenElements(lat.value, lng.value)
      // Also dispatch coordinates event for other components
      GlobalEventBus.dispatch('coordinates', {
        lat: locationData.lat,
        lng: locationData.lng
      })
    }
  })

  GlobalEventBus.subscribe('coordinates', positionData => {
    lng.value = parseFloat(positionData.lng)
    lat.value = parseFloat(positionData.lat)
    position.value = `${lat.value.toFixed(4)}, ${lng.value.toFixed(4)}`
    setHiddenElements(lat.value, lng.value)
  })

  // Set initial location if provided
  if (props.pointData?.entity?.location_lat && props.pointData?.entity?.location_long) {
    lat.value = parseFloat(props.pointData.entity.location_lat)
    lng.value = parseFloat(props.pointData.entity.location_long)
    position.value = `${lat.value.toFixed(4)}, ${lng.value.toFixed(4)}`
    setHiddenElements(lat.value, lng.value)
  }
})

// Methods
const openModal = () => {
  console.log('props.pointData', props.pointData)

  ModalService.spawn('standardized-location-modal', {
    locationType: props.pointData?.location_type || null,
    initialLocation: {
      lat: lat.value ? parseFloat(lat.value) : null,
      lng: lng.value ? parseFloat(lng.value) : null,
      countryCode: props.pointData?.country?.iso3_code
    },
    entity: props.pointData?.entity || null,
    trainingCenter: props.pointData?.training_center || null,
    targetArea: props.pointData?.target_area || null,
    kestrelApiKey: props.pointData?.kestrel_api_key,
    kestrelApiUrl: props.pointData?.kestrel_api_url,
  })
}

const setHiddenElements = (lat, lng) => {
  const elements = {
    'location-lat': lat,
    'location-long': lng
  }

  Object.entries(elements).forEach(([id, value]) => {
    const element = document.getElementById(id)
    if (element) element.value = value
  })
}
</script>

<style scoped>
.location-selector-card {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  margin-top: 4px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  min-height: 72px;
}

.location-selector-card:hover {
  border-color: #007bff;
  background: linear-gradient(135deg, #e6f3ff 0%, #cce5ff 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
}

.location-selector-card:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.2);
}

.location-selector-card.location-selected {
  border-style: solid;
  border-color: #10b981;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
}

.location-selector-card.location-selected:hover {
  border-color: #059669;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
}

.location-empty-state,
.location-selected-state {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
}

.location-icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #e5e7eb;
  flex-shrink: 0;
  transition: all 0.25s ease;
}

.location-selector-card:hover .location-icon-container {
  background: #b3d7ff;
}

.location-icon-container.selected {
  background: #10b981;
}

.location-selector-card.location-selected:hover .location-icon-container.selected {
  background: #059669;
}

.location-icon {
  width: 24px;
  height: 24px;
  color: #6b7280;
  transition: color 0.25s ease;
}

.location-selector-card:hover .location-icon {
  color: #0056b3;
}

.location-icon-container.selected .location-icon {
  color: white;
}

.checkmark-badge {
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.checkmark-badge svg {
  width: 12px;
  height: 12px;
  color: #10b981;
}

.location-text-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.location-action-text {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  transition: color 0.25s ease;
}

.location-selector-card:hover .location-action-text {
  color: #007bff;
}

.location-helper-text {
  font-size: 13px;
  color: #6b7280;
}

.location-confirmed-text {
  font-size: 15px;
  font-weight: 600;
  color: #059669;
}

.location-edit-text {
  font-size: 13px;
  color: #6b7280;
}

.location-chevron {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  opacity: 0.5;
  transition: all 0.25s ease;
}

.location-chevron svg {
  width: 20px;
  height: 20px;
  color: #9ca3af;
}

.location-selector-card:hover .location-chevron {
  opacity: 1;
  transform: translateX(3px);
}

.location-selector-card:hover .location-chevron svg {
  color: #007bff;
}

.location-selector-card.location-selected:hover .location-chevron svg {
  color: #059669;
}
</style>
